/**
 * @author Jersson Stiven Morales Alza
 * @Copyright (c) Infotrack. All rights reserved.
 */
import { useProveedor } from '@infotrack/presentacion-componentes/proveedorEstado';
import paginacionRemota from '@infotrack/presentacion-componentes/tabla/paginacionRemota';
import Texto from '@infotrack/presentacion-componentes/texto';
import { TipoFormulario } from '@infotrack/presentacion-transversales/interfacesComunes';
import { useInputState } from '@infotrack/presentacion-utilitarios/hooks';
import { manejadorDialogoGlobal } from '@infotrack/presentacion-utilitarios/manejadoresComponentes';
import IMunicipio from 'Infotrack@Modelos/administracion/municipio/entidades/municipio';
import IClientes from 'Infotrack@Modelos/smartStock/clientes/entidades/clientes';
import IFiltrosClientes from 'Infotrack@Modelos/smartStock/clientes/entidades/filtrosClientes';
import { IEstadoGlobal } from 'Infotrack@Reductores/interfacesReductores';
import React from 'react';
import { connect } from 'react-redux';
import ClientesCM from '../controladorModelo/clientesCM';
import EntidadesClientesCM from '../controladorModelo/entidadesClientesCM';
import { IInputsFiltrosClientes, ITableClientesCVProps } from '../interfaces';
import { acciones, IEstadoClientes } from '../reductorClientes';
import ClientesVista from '../vista/tablaClientesVista';
import { useSelector } from 'react-redux';
import { estadoActivo } from 'Infotrack@Transversales/constantes/ConstantesEstados';

const clientesCM = new ClientesCM();
const entidadesClientesCM = new EntidadesClientesCM();
const valorInicialFiltros: IInputsFiltrosClientes = {
    Ciudad: null,
    TipoClienteId: '',
    NombreContacto: '',
    DescripcionCliente: '',
};

const TableClientesCV: React.FunctionComponent<ITableClientesCVProps> = ({ IdEmpresa }) => {
    const cargaInicial = React.useRef(true);
    const refTabla = React.createRef<any>();
    const idEmpresa = useSelector((estado: IEstadoGlobal) => estado.estadoAutenticacion.usuarioInformacion!.IdEmpresa);
    const [{ entidadesClientes, recargarTabla }, dispatch] = useProveedor<IEstadoClientes>();
    const { alCambiarValor, reiniciar, setValor, valor } = useInputState(valorInicialFiltros);

    React.useEffect(() => {
        if (recargarTabla || cargaInicial.current) {
            cargarClientes();
            if (recargarTabla) {
                dispatch({ type: acciones.RECARGAR_TABLA });
            }
        }
        cargaInicial.current = false;
    });

    React.useEffect(() => {
    cargarEntidadesCliente();
    }, []);

    const manejarFormulario = (tipoFormulario: TipoFormulario, entidadClientes?: IClientes) => {
        dispatch({ type: acciones.CAMBIAR_ESTADO_FORMULARIO, payload: { entidadClientes, tipoFormulario } });
    };

    const confirmarEliminacion = (clienteAEliminar: IClientes) => {
        manejadorDialogoGlobal({
            accionCancelar: () => manejadorDialogoGlobal({ estado: false }),
            accionConfirmar: () => eliminarClientes(clienteAEliminar),
            estado: true,
            mensaje: clienteAEliminar.Principal ? (
                <Texto id={'clientes.alerta.eliminarClientePrincipal'} />
            ) : (
                <Texto id={'alerta.confirmacionEliminacion'} />
            ),
            mostrarCancelar: true,
            tipoDialogo: 'Advertencia',
            titulo: <Texto id="titulo.advertencia" />,
        });
    };

    const eliminarClientes = (clienteAEliminar: IClientes) => {
        manejadorDialogoGlobal({ estado: false });
        clientesCM.eliminarClientes(clienteAEliminar).then(() => {
            dispatch({ type: acciones.RECARGAR_TABLA });
        });
    };

    const cargarClientes = (limpiarFiltros?: boolean) => {
        if (limpiarFiltros) {
            reiniciar();
        }
        refTabla.current!.onQueryChange({ page: 0, pageSize: 10, orderDirection: 'desc', orderBy: null });
    };

    const mapearFiltros = (): IFiltrosClientes => {
        const { Ciudad, DescripcionCliente, TipoClienteId, NombreContacto } = valor;
        return {
            EmpresaId: idEmpresa,
            CiudadId: Ciudad ? Number(Ciudad.IdMunicipio) : undefined,
            TipoClienteId: TipoClienteId ? Number(TipoClienteId) : undefined,
            NombreContacto: NombreContacto ? NombreContacto : undefined,
            DescripcionCliente: DescripcionCliente ? DescripcionCliente : undefined,
        };
    };
    const consultaClientes = paginacionRemota(
        clientesCM.consultarListaClientesPaginadaCompuesta,
        mapearFiltros(),
        'DescripcionCliente'
    );

    const cargarEntidadesCliente = () => {
        entidadesClientesCM
            .consultarEntidadesCliente(IdEmpresa)
            .then(({ tiposClientes: tiposClientesCargados, municipios: municipiosCargados }) => {
                dispatch({
                    type: acciones.ESTABLECER_ENTIDADES_CLIENTE,
                    payload: { municipios: municipiosCargados, tiposClientes: tiposClientesCargados },
                });
            });
    };

    const alCambiarMunicipio = (nuevoMunicipio: IMunicipio) => {
        setValor({ ...valor, Ciudad: nuevoMunicipio });
    };

    return (
        <ClientesVista
            alCambiarMunicipio={alCambiarMunicipio}
            alCambiarValor={alCambiarValor}
            consultaClientes={consultaClientes}
            cargarClientes={cargarClientes}
            confirmarEliminacion={confirmarEliminacion}
            entidadesCliente={entidadesClientes}
            manejarFormulario={manejarFormulario}
            refTabla={refTabla}
            filtros={valor}
        />
    );
};

const estadoAPropiedades = ({ estadoAutenticacion: { usuarioInformacion } }: IEstadoGlobal) => ({
    IdEmpresa: usuarioInformacion!.IdEmpresa,
});

export default connect<ITableClientesCVProps, null, any, IEstadoGlobal>(estadoAPropiedades, null)(TableClientesCV);
